import { useLayoutEffect } from 'react';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useSafeNavigation, useSafeRouteParams, useSafeRouter } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import {
  type AuthRedirectTarget,
  type AuthStackParamList,
} from '../../../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook that redirects the user to the new "Join or Sign In" screen if
 * the corresponding feature flag is enabled.
 *
 * Can be used on legacy authentication routes.
 */
export const useAzureAuthScreenRedirect = () => {
  const navigation = useSafeNavigation<NativeStackNavigationProp<AuthStackParamList>>();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const params = useSafeRouteParams<AuthRouteParams>();
  const { track } = useTelemetry();

  const isHybridAuthFlowEnabled = useFeatureFlag('cels-2041-auth-hybrid-flow', {
    listenForChanges: true,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const routeParams = params ?? ROUTE_PARAMS_FALLBACK;

    if (isHybridAuthFlowEnabled) {
      // NOTE: We track the "view" event here since `JoinOrSignScreen` is also used as a redirect target
      //       when employing authentication "Redirect" flow, which can result in additional telemetry events.
      track('join-or-sign-in.view', { referrer: routeParams.redirect });
    }

    if (isExpoRouterEnabled) {
      router?.replace({
        pathname: '/(auth)/join-or-sign-in',
        params: routeParams,
      });
      return;
    }

    navigation?.replace('JoinOrSignIn', params ?? ROUTE_PARAMS_FALLBACK);
  }, [navigation, router, isExpoRouterEnabled, params, track, isHybridAuthFlowEnabled]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ROUTE_PARAMS_FALLBACK: AuthRouteParams = {};

// ─── Types ───────────────────────────────────────────────────────────────────

type AuthRouteParams = {
  redirect?: AuthRedirectTarget;
};
